import { resolveFinancialDiscountLineItems, sumFinancialDiscountLineItems } from '../../../utils/financialDiscounts.js';

// Owner-folio display only. These values never feed payments or reconciliation.
const cents = (value) => {
  if (value == null || value === '' || typeof value === 'boolean') return null;
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number * 100) : null;
};

export function resolveGuestPaymentHeading({ guestPaid, guestDiscountsTotal, isVrbo, isLegacyVRBOiCal }) {
  const paid = cents(guestPaid?.totalCollected);
  const sourceTotal = cents(guestPaid?.totalReservationAmount);
  const parts = [guestPaid?.baseRate, guestPaid?.cleaningFee, guestPaid?.touristTax, guestDiscountsTotal].map(cents);
  const addOns = Array.isArray(guestPaid?.addOnFees) ? guestPaid.addOnFees.map((fee) => cents(fee?.amount)) : [];
  const channelFee = cents(guestPaid?.guestChannelFee) ?? 0;
  const hasCompleteBreakdown = [...parts, ...addOns].every((amount) => amount != null);
  const itemizedTotal = hasCompleteBreakdown ? [...parts, ...addOns].reduce((sum, amount) => sum + amount, channelFee) : null;
  let displayTotal = sourceTotal ?? (isVrbo ? itemizedTotal : null);

  // The integrated VRBO cash total can exclude the separately displayed guest
  // channel fee. Reuse the existing itemized charge total only when that exact
  // difference explains it; never add the fee twice or replace another mismatch.
  if (isVrbo && !isLegacyVRBOiCal && sourceTotal != null && itemizedTotal != null
      && channelFee > 0 && itemizedTotal - sourceTotal === channelFee) {
    displayTotal = itemizedTotal;
  }

  // A display comparison does not assert an unpaid balance or authorize payment.
  const showReservationAmount = Boolean(isVrbo && paid != null && displayTotal != null
    && displayTotal > 0 && Math.abs(displayTotal - paid) > 1);
  return {
    label: showReservationAmount ? 'Paid to Date' : 'Total Guest Payment',
    showReservationAmount,
    reservationAmount: displayTotal == null ? null : displayTotal / 100,
  };
}

// Adapt the persisted mobile contract to the same display rule as the desktop folio.
export function resolveGuestPaymentHeadingFromSummary(summary) {
  const g = summary?.guest_paid ?? {};
  const channel = summary?.channel ?? {};
  const discounts = resolveFinancialDiscountLineItems({
    discountLineItems: g.discount_line_items, discountsTotal: g.guest_discounts_total,
    legacyCouponDiscount: g.guest_coupon_discount, legacyWeeklyDiscount: g.guest_weekly_discount,
  });
  return resolveGuestPaymentHeading({
    guestPaid: {
      baseRate: g.guest_base_rate, cleaningFee: g.guest_cleaning_fee, touristTax: g.guest_tourist_tax,
      guestChannelFee: g.guest_channel_fee, addOnFees: g.add_on_fees,
      totalCollected: g.guest_total_paid, totalReservationAmount: g.total_reservation_amount,
    },
    guestDiscountsTotal: sumFinancialDiscountLineItems(discounts),
    isVrbo: channel.master_channel_key === 'vrbo',
    isLegacyVRBOiCal: channel.master_channel_key === 'vrbo' && channel.channel_subtype === 'ical',
  });
}
